import React, { useEffect, useState, useCallback } from "react";
import { api } from "../../lib/api";
import { toast } from "sonner";
import { ShieldSlash, Prohibit, Trash, PaperPlaneTilt, Warning } from "@phosphor-icons/react";

/**
 * Opt-outs manager.
 * When commerçants reply STOP to outreach emails (or contact you to be
 * removed), paste their emails/phones here — they will be permanently
 * excluded from any future automated send.
 */
export default function OptOutsPanel() {
  const [items, setItems] = useState([]);
  const [total, setTotal] = useState(0);
  const [status, setStatus] = useState(null);
  const [bulk, setBulk] = useState("");
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const [ls, st] = await Promise.all([
        api.get("/admin/opt-outs"),
        api.get("/admin/outreach/status"),
      ]);
      setItems(ls.data.items || []);
      setTotal(ls.data.total || 0);
      setStatus(st.data);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Erreur chargement");
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { load(); }, [load]);

  const submit = async () => {
    if (!bulk.trim()) return;
    setSaving(true);
    try {
      const r = await api.post("/admin/opt-outs", { bulk, reason: "manual_stop_reply" });
      const d = r.data;
      toast.success(`+${d.added_emails} emails, +${d.added_phones} téléphones ajoutés (total ${d.total_opt_outs})`);
      setBulk("");
      await load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Erreur");
    } finally {
      setSaving(false);
    }
  };

  const remove = async (it) => {
    if (!window.confirm("Retirer ce contact de la liste ? Il pourra à nouveau recevoir nos emails.")) return;
    try {
      await api.delete("/admin/opt-outs", { params: it.email ? { email: it.email } : { phone: it.phone } });
      await load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Erreur");
    }
  };

  return (
    <div data-testid="admin-opt-outs">
      <div className="bg-card border border-border rounded-sm p-4 mb-4">
        <div className="flex items-start justify-between gap-3 flex-wrap">
          <div>
            <div className="label-uppercase text-primary flex items-center gap-1.5 mb-1">
              <ShieldSlash size={14} weight="fill"/> Opt-outs — Liste STOP
            </div>
            <h2 className="font-display text-lg font-medium">Ne plus jamais contacter</h2>
            <p className="text-xs text-muted-foreground mt-1 max-w-2xl leading-relaxed">
              Colle ici les emails/téléphones des commerçants qui t&apos;ont demandé d&apos;arrêter (réponses <b>STOP</b>,
              plaintes, demandes RGPD). Ils seront <b>définitivement exclus</b> de tout envoi automatique.
              Auto-détection email vs téléphone. Doublons ignorés.
            </p>
          </div>
          <div className="text-right">
            <div className="text-[10px] uppercase tracking-widest text-muted-foreground">Contacts bloqués</div>
            <div className="font-display text-3xl font-medium tabular-nums" data-testid="opt-outs-total">{total}</div>
          </div>
        </div>
        {status && (
          <div className={`mt-3 flex items-center gap-2 text-xs px-3 py-2 rounded-sm border ${
            status.enabled
              ? "bg-destructive/10 border-destructive/30 text-destructive"
              : "bg-primary/10 border-primary/30 text-primary"
          }`} data-testid="outreach-status-banner">
            {status.enabled ? <PaperPlaneTilt size={14} weight="fill"/> : <Prohibit size={14} weight="fill"/>}
            <span>
              <b>Outreach automatique : {status.enabled ? "ACTIF" : "DÉSACTIVÉ"}</b>
              {status.enabled
                ? ` — cap ${status.daily_cap}/jour. Les envois quotidiens partent à 09:00 UTC.`
                : " — aucun envoi automatique ne partira. Réactivable via env `OUTREACH_ENABLED=1`."}
            </span>
          </div>
        )}
      </div>

      <div className="bg-card border border-border rounded-sm p-4 mb-4">
        <label className="block text-sm font-semibold mb-2">Ajouter des opt-outs</label>
        <textarea
          value={bulk}
          onChange={(e) => setBulk(e.target.value)}
          placeholder={"contact@exemple.fr\ninfo@boulangerie.fr\n0389123456\n0612345678"}
          rows={5}
          className="w-full bg-background border border-border rounded-sm px-3 py-2 text-sm font-mono"
          data-testid="opt-outs-bulk-input"
        />
        <div className="flex items-center justify-between mt-2">
          <div className="text-[11px] text-muted-foreground">
            <Warning size={11} className="inline mr-1" weight="fill"/>
            Un contact par ligne, ou séparés par virgule/espace. Emails et numéros mélangés OK.
          </div>
          <button
            onClick={submit}
            disabled={saving || !bulk.trim()}
            className="bg-foreground text-background text-xs font-semibold uppercase tracking-wider px-4 py-2 rounded-sm disabled:opacity-40 hover:opacity-90 transition"
            data-testid="opt-outs-submit"
          >
            {saving ? "Ajout…" : "Ajouter à la liste"}
          </button>
        </div>
      </div>

      {loading ? (
        <div className="text-sm text-muted-foreground py-6 text-center">Chargement…</div>
      ) : items.length === 0 ? (
        <div className="bg-accent/20 border border-dashed border-border rounded-sm p-8 text-center text-sm text-muted-foreground" data-testid="opt-outs-empty">
          Aucun opt-out enregistré. Bonne nouvelle : personne ne t&apos;a encore dit STOP.
        </div>
      ) : (
        <div className="bg-card border border-border rounded-sm overflow-hidden">
          <table className="w-full text-xs">
            <thead className="text-left text-[10px] uppercase tracking-widest text-muted-foreground border-b border-border bg-accent/20">
              <tr>
                <th className="px-3 py-2 w-36">Ajouté le</th>
                <th className="px-3 py-2">Contact</th>
                <th className="px-3 py-2 w-24 text-center">Type</th>
                <th className="px-3 py-2 w-40">Motif</th>
                <th className="px-3 py-2 w-16"></th>
              </tr>
            </thead>
            <tbody className="divide-y divide-border">
              {items.map((it) => (
                <tr key={it.id || it.email || it.phone} data-testid={`opt-out-row-${it.email || it.phone}`}>
                  <td className="px-3 py-2 font-mono text-[10px] text-muted-foreground">
                    {it.created_at ? new Date(it.created_at).toLocaleString("fr-FR", { dateStyle: "short", timeStyle: "short" }) : "—"}
                  </td>
                  <td className="px-3 py-2 font-mono">{it.email || it.phone}</td>
                  <td className="px-3 py-2 text-center">
                    <span className={`inline-flex items-center gap-1 border rounded-sm px-1.5 py-0.5 text-[9px] font-semibold uppercase ${
                      it.email ? "bg-primary/10 text-primary border-primary/30" : "bg-secondary/20 text-secondary-foreground border-secondary/40"
                    }`}>
                      {it.email ? "Email" : "Tél."}
                    </span>
                  </td>
                  <td className="px-3 py-2 text-muted-foreground">{it.reason || "—"}</td>
                  <td className="px-3 py-2 text-right">
                    <button
                      onClick={() => remove(it)}
                      className="text-muted-foreground hover:text-destructive"
                      title="Retirer de la liste"
                      data-testid={`opt-out-remove-${it.email || it.phone}`}
                    >
                      <Trash size={14}/>
                    </button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
}
